import {
  EMPTY_LIST,
  EMPTY_OBJECT,
  EMPTY_STRING,
} from '../../../../constants/common'
import {
  isAIConversationMessageEvent,
  isAIConversationMessageFailed,
  isLegacyStoppedReply,
} from '../../../../utils/aiAgents'
import { isValueNumberType } from '../../../../utils/common'
import { GretaAIChatStatus } from '../../GretaAIChat.constants'
import { getIsGretaAIChatAssistantRole } from '../../GretaAIChat.utils'
import {
  getGretaAIChatRenderableTurns,
  getIsGretaAIChatUserRole,
} from '../../GretaAIChatMessage'

function getIsGretaAIChatEventMessage(message) {
  return (
    getIsGretaAIChatAssistantRole(message.role) &&
    (isAIConversationMessageEvent(message) ||
      isLegacyStoppedReply(message.content))
  )
}

function getGretaAIChatLatestSeq(messages) {
  return Math.max(...messages.map(message => message.seq))
}

function getIsGretaAIChatSameQuestion(message, question) {
  return getIsGretaAIChatUserRole(message.role) && message.text === question
}

// The question's copies the server saved after the feed's newest synced message (`syncedSeq`).
function getGretaAIChatSavedQuestionIndexes({ loaded, question, syncedSeq }) {
  return loaded.flatMap((message, index) =>
    getIsGretaAIChatSameQuestion(message, question) && message.seq > syncedSeq
      ? [index]
      : EMPTY_LIST
  )
}

function getGretaAIChatQuestionOccurrence(messages, question) {
  return messages.filter(
    message =>
      message.isPendingId && getIsGretaAIChatSameQuestion(message, question)
  ).length
}

// The n-th unsynced ask of a question is saved once the page has n copies of it.
function getIsGretaAIChatLocalQuestionSaved({
  index,
  loaded,
  locals,
  syncedSeq,
}) {
  const { text } = locals[index]

  return (
    getGretaAIChatSavedQuestionIndexes({ loaded, question: text, syncedSeq })
      .length >=
    getGretaAIChatQuestionOccurrence(locals.slice(0, index + 1), text)
  )
}

function extractGretaAIChatListItems(response) {
  const data = response?.data

  if (Array.isArray(data)) {
    return data
  }

  if (Array.isArray(data?.results)) {
    return data.results
  }

  if (Array.isArray(data?.results?.objects)) {
    return data.results.objects
  }

  return EMPTY_LIST
}

export function getIsGretaAIChatHistoryLoadable(activeChatId, loadedChatId) {
  return Boolean(activeChatId) && activeChatId !== loadedChatId
}

export function getHasNextGretaAIChatMessagesPage(response) {
  const { page, pages } = response?.data ?? EMPTY_OBJECT

  return isValueNumberType(page) && isValueNumberType(pages) && page < pages
}

export function getGretaAIChatDeliveredTurn(payloadMessages) {
  const earlier = payloadMessages.slice(0, -1)
  const question = payloadMessages.at(-1).text

  return {
    occurrence: getGretaAIChatQuestionOccurrence(earlier, question) + 1,
    question,
    syncedSeq: getGretaAIChatLatestSeq(
      earlier.filter(message => !message.isPendingId)
    ),
  }
}

// The turn's own copy of the question is saved and already has an answer.
export function getHasGretaAIChatDeliveredTurn(
  loaded,
  { occurrence, question, syncedSeq }
) {
  const questionIndex = getGretaAIChatSavedQuestionIndexes({
    loaded,
    question,
    syncedSeq,
  })[occurrence - 1]

  return (
    questionIndex !== undefined &&
    loaded
      .slice(questionIndex + 1)
      .some(message => getIsGretaAIChatAssistantRole(message.role))
  )
}

// Synced feed messages newer than the page stay. Local ones stay too, except a delivered turn's
// (up to the last local reply) once the page holds it, and a question the page already saved.
export function reconcileGretaAIChatLoadedMessages({
  current,
  isTurnLagging,
  loaded,
}) {
  const pageSeq = getGretaAIChatLatestSeq(loaded)
  const synced = current.filter(message => !message.isPendingId)
  const syncedSeq = getGretaAIChatLatestSeq(synced)
  const locals = current.filter(message => message.isPendingId)
  const lastLocalReplyIndex = locals.findLastIndex(message =>
    getIsGretaAIChatAssistantRole(message.role)
  )
  const keptFromIndex = isTurnLagging ? 0 : lastLocalReplyIndex + 1

  return [
    ...loaded,
    ...synced.filter(message => message.seq > pageSeq),
    ...locals.filter(
      (message, index) =>
        index >= keptFromIndex &&
        (!getIsGretaAIChatUserRole(message.role) ||
          !getIsGretaAIChatLocalQuestionSaved({
            index,
            loaded,
            locals,
            syncedSeq,
          }))
    ),
  ]
}

// New messages shift the page numbers, so an older page can overlap the feed's oldest ones.
export function prependGretaAIChatOlderMessages(older, current) {
  const feedIds = new Set(current.map(message => message.id))

  return [...older.filter(message => !feedIds.has(message.id)), ...current]
}

export function getGretaAIChatLoadedStatus({
  isFailed,
  isTimedOut,
  isTurnInFlight,
}) {
  if (isTurnInFlight) {
    return GretaAIChatStatus.Thinking
  }

  if (isTimedOut && !isFailed) {
    return GretaAIChatStatus.Timeout
  }

  return isFailed ? GretaAIChatStatus.Failed : GretaAIChatStatus.Idle
}

export function getIsGretaAIChatHistoryFailed(response) {
  const turns = getGretaAIChatRenderableTurns(
    extractGretaAIChatListItems(response)
  )

  return isAIConversationMessageFailed(turns[0])
}

export function mapGretaAIChatMessages(response) {
  return getGretaAIChatRenderableTurns(extractGretaAIChatListItems(response))
    .filter(message => !getIsGretaAIChatEventMessage(message))
    .map(message => ({
      createdAt: message.created ?? message.updated,
      feedback: message.feedback ?? null,
      id: message.id,
      role: message.role,
      seq: message.seq,
      text: message.content ?? EMPTY_STRING,
    }))
    .reverse()
}
